/**
 * EN-1503: collapsed state of the pig run selection card (the Leaflet layers
 * control at the top left of the map where the user picks which active pig
 * run is visible).
 *
 * The state helpers are pure, so they can be unit tested without a DOM or a
 * map. mountPigRunSelectionToggle holds the DOM wiring; it uses only plain DOM
 * APIs (no Leaflet, no Vue), so it can be tested against a real Leaflet layers
 * control in a bare page.
 */

/** localStorage key holding the user's last choice ('1' collapsed, '0' expanded). */
export const PIG_RUN_SELECTION_COLLAPSED_KEY =
  'propipe.pigRunSelectionCollapsed';

/** Label for the collapsed button when no pig run is selected. */
export const PIG_RUN_SELECTION_DEFAULT_LABEL = 'Pig runs';

/**
 * id of the control's option list, referenced by both buttons' aria-controls.
 * It is a document-wide id; there is one pig run selection card per page.
 */
export const PIG_RUN_SELECTION_LIST_ID = 'pigRunSelectionList';

/** Class mountPigRunSelectionToggle adds to the control container. */
export const PIG_RUN_SELECTION_CONTROL_CLASS = 'pigRunSelectionControl';

/** Class on the control container while the card is collapsed. */
export const PIG_RUN_SELECTION_COLLAPSED_CLASS = 'pigRunSelectionCollapsed';

/** Tooltip and accessible name of the compact (collapsed) button. */
export function pigRunSelectionExpandTitle(label: string): string {
  return `Expand pig run selection: ${label}`;
}

/** Minimal Storage surface, so tests can pass a fake or a throwing store. */
export type CollapseStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Initial collapsed state: the saved choice wins; with no saved choice the
 * card starts collapsed on phones (under 600px) and expanded otherwise.
 */
export function initialPigRunSelectionCollapsed(
  storedCollapsed: boolean | null,
  isMobile: boolean
): boolean {
  if (storedCollapsed !== null) return storedCollapsed;
  return isMobile;
}

/** Text for the collapsed button: the selected run's name, or 'Pig runs'. */
export function pigRunSelectionLabel(
  selectedRunName: string | null | undefined
): string {
  const name = (selectedRunName ?? '').trim();
  return name || PIG_RUN_SELECTION_DEFAULT_LABEL;
}

function defaultStorage(): CollapseStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    // Accessing localStorage can throw (privacy mode, blocked cookies).
    return null;
  }
}

/**
 * Saved choice, or null when nothing (or nothing valid) is saved or storage is
 * unavailable.
 */
export function readPigRunSelectionCollapsed(
  storage: CollapseStorage | null = defaultStorage()
): boolean | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(PIG_RUN_SELECTION_COLLAPSED_KEY);
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
  } catch {
    return null;
  }
}

/** Saves the choice; returns false when storage is unavailable or full. */
export function writePigRunSelectionCollapsed(
  collapsed: boolean,
  storage: CollapseStorage | null = defaultStorage()
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(PIG_RUN_SELECTION_COLLAPSED_KEY, collapsed ? '1' : '0');
    return true;
  } catch {
    return false;
  }
}

export interface PigRunSelectionToggle {
  readonly collapseBtn: HTMLButtonElement;
  readonly expandBtn: HTMLButtonElement;
  /** Shows the given state and puts label in the compact button, as text. */
  render(collapsed: boolean, label: string): void;
  /**
   * Undoes the mount: removes the click handlers, the buttons, the
   * pigRunSelectionControl and collapsed classes, and the list id it set.
   */
  destroy(): void;
}

/**
 * Adds the minimize button and the compact expand button to the layers
 * control's container, just before its option list (so they come first in
 * the card's tab order). The buttons sit directly in the container (not in
 * its option list), which Leaflet keeps when addBaseLayer / addOverlay /
 * removeLayer rebuild the list, so they and the collapsed class survive those
 * rebuilds.
 *
 * Clicking a button calls onToggle with the requested state; the caller saves
 * it and calls render. Focus then moves to the other button, which render has
 * just made visible, so keyboard users are not left on a hidden element.
 *
 * No per-button click/scroll stopping is needed: Leaflet's
 * Control.Layers._initLayout already calls disableClickPropagation and
 * disableScrollPropagation on this container.
 *
 * The label only ever reaches the DOM through textContent, the title property
 * and setAttribute, never as HTML.
 */
export function mountPigRunSelectionToggle(
  container: HTMLElement,
  onToggle: (collapsed: boolean) => void
): PigRunSelectionToggle {
  const doc = container.ownerDocument;
  container.classList.add(PIG_RUN_SELECTION_CONTROL_CLASS);
  const list = container.querySelector(
    '.leaflet-control-layers-list'
  ) as HTMLElement | null;
  if (list) list.id = PIG_RUN_SELECTION_LIST_ID;

  const icon = (name: string) => {
    const i = doc.createElement('i');
    i.className = 'q-icon material-icons';
    i.setAttribute('aria-hidden', 'true');
    i.textContent = name;
    return i;
  };

  const collapseBtn = doc.createElement('button');
  collapseBtn.type = 'button';
  collapseBtn.className = 'pigRunSelectionCollapseBtn';
  collapseBtn.title = 'Minimize';
  collapseBtn.setAttribute('aria-label', 'Collapse pig run selection');
  collapseBtn.setAttribute('aria-controls', PIG_RUN_SELECTION_LIST_ID);
  // Fixed: this button is only visible while the card is expanded.
  collapseBtn.setAttribute('aria-expanded', 'true');
  collapseBtn.appendChild(icon('minimize'));

  const expandBtn = doc.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'pigRunSelectionExpandBtn';
  expandBtn.setAttribute('aria-controls', PIG_RUN_SELECTION_LIST_ID);
  // Fixed: this button is only visible while the card is collapsed.
  expandBtn.setAttribute('aria-expanded', 'false');
  const expandLabel = doc.createElement('span');
  expandLabel.className = 'pigRunSelectionExpandLabel';
  expandBtn.append(icon('directions_run'), expandLabel, icon('open_in_full'));

  // Insert the buttons just before the option list, so the tab order within
  // the card is: minimize (or, when collapsed, the compact expand button),
  // then the run options, then the next map control. DOM order sets the tab
  // order; no tabindex is used.
  container.insertBefore(collapseBtn, list);
  container.insertBefore(expandBtn, list);

  const onCollapseClick = () => {
    onToggle(true);
    expandBtn.focus();
  };
  const onExpandClick = () => {
    onToggle(false);
    collapseBtn.focus();
  };
  collapseBtn.addEventListener('click', onCollapseClick);
  expandBtn.addEventListener('click', onExpandClick);

  return {
    collapseBtn,
    expandBtn,
    render(collapsed: boolean, label: string) {
      container.classList.toggle(PIG_RUN_SELECTION_COLLAPSED_CLASS, collapsed);
      const title = pigRunSelectionExpandTitle(label);
      expandBtn.title = title;
      expandBtn.setAttribute('aria-label', title);
      expandLabel.textContent = label;
    },
    destroy() {
      collapseBtn.removeEventListener('click', onCollapseClick);
      expandBtn.removeEventListener('click', onExpandClick);
      collapseBtn.remove();
      expandBtn.remove();
      container.classList.remove(
        PIG_RUN_SELECTION_CONTROL_CLASS,
        PIG_RUN_SELECTION_COLLAPSED_CLASS
      );
      if (list?.id === PIG_RUN_SELECTION_LIST_ID) {
        list.removeAttribute('id');
      }
    },
  };
}
